import { useMemo, useState } from 'react';
import type { z } from 'zod';

import { fieldErrors, firstInvalid, visibleErrors } from '~/lib/validation';

/**
 * Zod validation timing for a local-state form: a field's error shows after it is blurred and
 * then follows every change; a submit shows all of them and focuses the first invalid field.
 * `id(field)` is the DOM id of that field's control (default: the field name).
 *
 * `<TextField onBlur={() => v.touch('name')} error={v.errors.name} />`; in the submit handler,
 * `if (!v.submit()) return;`.
 */
export function useFormValidation(
  schema: z.ZodType,
  values: unknown,
  id: (field: string) => string = (field) => field,
) {
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const all = useMemo(() => fieldErrors(schema, values), [schema, values]);

  return {
    errors: visibleErrors(all, touched, submitted),
    touch: (field: string) => setTouched((current) => new Set(current).add(field)),
    /** True when valid; otherwise shows every error and focuses the first invalid field. */
    submit: (): boolean => {
      setSubmitted(true);
      const first = firstInvalid(all);
      if (first) document.getElementById(id(first))?.focus();
      return !first;
    },
    reset: () => {
      setTouched(new Set());
      setSubmitted(false);
    },
  };
}
